import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatRand, displayPrice, isSoldOut, resolveImageUrl } from "../lib/api";
import { useCart } from "../lib/cart";

const COLOR_MAP = {
  "Black": "#0a0a0a",
  "Off-Black": "#1c1c1c",
  "Ink Black": "#101010",
  "Charcoal": "#2c2c2c",
  "Bone": "#e8e2d3",
  "Vintage White": "#f0ece1",
  "Cream": "#efe9d8",
  "Sand": "#d6c8a8",
  "Khaki": "#9a8a6a",
  "Stone": "#b9b1a4",
  "Cement": "#8a857d",
  "Faded Olive": "#5d6347",
  "Espresso": "#3a2a20",
  "Brown": "#5a3d2b",
  "Washed Black": "#222020",
};

const ProductDetail = ({ membersOnly = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [size, setSize] = useState(null);
  const [color, setColor] = useState(null);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    const path = membersOnly ? `/members/products/${id}` : `/products/${id}`;
    api
      .get(path)
      .then((r) => {
        setProduct(r.data);
        if (r.data.sizes?.length) setSize(r.data.sizes[0]);
        if (r.data.colors?.length) setColor(r.data.colors[0]);
      })
      .catch(() => {
        toast.error("Product not found");
        navigate(-1);
      });
  }, [id, membersOnly, navigate]);

  const onSale = useMemo(
    () => product?.sale_price && product.sale_price < product.price,
    [product],
  );

  const addToCart = () => {
    if (!product) return;
    if (isSoldOut(product)) {
      toast.error("Sold out");
      return;
    }
    addItem({
      product_id: product.id,
      name: product.name,
      image: product.images?.[0],
      price: displayPrice(product),
      quantity: 1,
      size,
      color,
    });
    toast.success("Added to cart");
  };

  if (!product) return null;

  return (
    <div className="section-pad py-12 grid gap-12 md:grid-cols-2 fade-in" data-testid="product-detail-page">
      <div className="space-y-4">
        <div className="overflow-hidden" style={{ background: "#fff", aspectRatio: "4/5" }}>
          <img src={product.images?.[activeImg]} alt={product.name} className="w-full h-full object-cover" data-testid="product-main-image" />
        </div>
        {product.images?.length > 1 && (
          <div className="grid grid-cols-4 gap-3">
            {product.images.map((src, i) => (
              <button
                key={src}
                onClick={() => setActiveImg(i)}
                className={`overflow-hidden border ${activeImg === i ? "border-black" : "border-transparent"}`}
                style={{ background: "#fff", aspectRatio: "1/1" }}
                data-testid={`product-thumb-${i}`}
              >
                <img src={resolveImageUrl(src)} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="md:pl-6">
        <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tighter leading-none" data-testid="product-name">
          {product.name}
        </h1>
        <div className="mt-6 flex items-baseline gap-4">
          {onSale ? (
            <>
              <span className="text-lg line-through text-black/40" data-testid="product-original-price">
                {formatRand(product.price)}
              </span>
              <span className="font-display text-3xl" data-testid="product-sale-price">
                {formatRand(product.sale_price)}
              </span>
            </>
          ) : (
            <span className="font-display text-3xl" data-testid="product-price">{formatRand(product.price)}</span>
          )}
        </div>

        {product.description && (
          <p className="mt-8 text-sm text-black/70 leading-relaxed max-w-md" data-testid="product-description">
            {product.description}
          </p>
        )}

        {product.colors?.length > 0 && (
          <div className="mt-10">
            <div className="flex gap-3 flex-wrap" data-testid="product-color-options">
              {product.colors.map((c) => (
                <button
                  key={c}
                  onClick={() => setColor(c)}
                  className={`swatch ${color === c ? "active" : ""}`}
                  style={{ background: COLOR_MAP[c] || "#888" }}
                  aria-label={c}
                  title={c}
                  data-testid={`product-color-${c.replace(/\s+/g, "-")}`}
                />
              ))}
            </div>
          </div>
        )}

        {product.sizes?.length > 0 && (
          <div className="mt-6">
            <div className="flex gap-2 flex-wrap" data-testid="product-size-options">
              {product.sizes.map((s) => (
                <button
                  key={s}
                  onClick={() => setSize(s)}
                  className={`size-tile ${size === s ? "active" : ""}`}
                  data-testid={`product-size-${s.replace(/\s+/g, "-")}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="mt-10">
          <button onClick={addToCart} disabled={isSoldOut(product)} className="btn-primary w-full" data-testid="product-add-to-cart">
            {isSoldOut(product) ? "Sold Out" : "Add to Cart"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
